import { AnnotationTypes } from '@atlaskit/adf-schema/annotation';
// oxlint-disable-next-line import/no-duplicates
import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics/api';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import {
	RESOLVE_METHOD,
	type VIEW_METHOD,
} from '@atlaskit/editor-common/analytics/types/inline-comment-events';
import type { AnnotationManager } from '@atlaskit/editor-common/annotation';
import { NATIVE_EMBED_EXTENSION_KEY } from '@atlaskit/editor-common/extensions/manifest-helpers';
import type { Command, CommandDispatch } from '@atlaskit/editor-common/types/command';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import {
	type EditorState,
	NodeSelection,
	type Transaction,
} from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { AnnotationPlugin } from '../annotationPluginType';
import { createCommand } from '../pm-plugins/plugin-factory';
import type {
	InlineCommentAction,
	InlineCommentMap,
	InlineCommentMouseData,
} from '../pm-plugins/types';
import { ACTIONS } from '../pm-plugins/types';
import {
	getPluginState,
	inlineCommentPluginKey,
	isSelectionValid,
	isSupportedBlockNode,
} from '../pm-plugins/utils';
import { AnnotationSelectionType } from '../types';
import type {
	AnnotationInfo,
	AnnotationProviders,
	InlineCommentAnnotationProvider,
	InlineCommentInputMethod,
	TargetType,
} from '../types';
import transform from './transform';
import { resetUserIntent, setUserIntent } from './utils';

export const updateInlineCommentResolvedState =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(partialNewState: InlineCommentMap, resolveMethod?: RESOLVE_METHOD): Command => {
		const command: InlineCommentAction = {
			type: ACTIONS.UPDATE_INLINE_COMMENT_STATE,
			data: partialNewState,
		};

		const allResolved = Object.values(partialNewState).every((state) => state);

		// TODO: EDF-716 - This is nuking the scroll into view which occurs
		// when a comment is resolved. The problem is this is called when either a collab user or the current user resolves a comment.
		// and the problem is since cc is just dispatching an event through the provider to resolve the comment and this
		// is not comming via NCS, we have not way to know if this is a local or remote transaction.
		// To quickly fix this problem to unblock live pages this will just stop the transaction causing a scroll when the
		// resolve method is set.
		const transformer = (tr: Transaction, state: EditorState) =>
			resolveMethod === RESOLVE_METHOD.CONSUMER ? tr.setMeta('scrollIntoView', false) : tr;

		if (resolveMethod && allResolved) {
			return createCommand(command, (tr: Transaction, state: EditorState) =>
				transformer(
					transform.addResolveAnalytics(editorAnalyticsAPI)(resolveMethod)(tr, state),
					state,
				),
			);
		}

		return createCommand(command, transformer);
	};

export const closeComponent = (): Command =>
	createCommand({
		type: ACTIONS.CLOSE_COMPONENT,
	});

export const clearDirtyMark = (): Command =>
	createCommand({
		type: ACTIONS.INLINE_COMMENT_CLEAR_DIRTY_MARK,
	});

export const setInlineCommentsFetched = (): Command =>
	createCommand({ type: ACTIONS.SET_INLINE_COMMENTS_FETCHED });

export const flushPendingSelections =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(canSetAsSelectedAnnotations: boolean, errorReason?: string): Command => {
		const command: InlineCommentAction = {
			type: ACTIONS.FLUSH_PENDING_SELECTIONS,
			data: {
				canSetAsSelectedAnnotations,
			},
		};

		if (!!errorReason) {
			return createCommand(command, (tr: Transaction, state: EditorState) =>
				transform.addPreemptiveGateErrorAnalytics(editorAnalyticsAPI)(errorReason)(tr, state),
			);
		}

		return createCommand(command);
	};

export const setPendingSelectedAnnotations = (
	selectedAnnotations: AnnotationInfo[],
	options?: {
		isOpeningMediaCommentFromToolbar?: boolean;
		selectAnnotationMethod?: VIEW_METHOD;
	},
): Command =>
	createCommand({
		type: ACTIONS.SET_PENDING_SELECTIONS,
		data: {
			selectedAnnotations,
			selectAnnotationMethod: options?.selectAnnotationMethod,
			isOpeningMediaCommentFromToolbar: options?.isOpeningMediaCommentFromToolbar,
		},
	});

export const setPendingSelectedAnnotation = (id: string): Command =>
	setPendingSelectedAnnotations([{ id, type: AnnotationTypes.INLINE_COMMENT }]);

const removeInlineCommentFromNode = (
	id: string,
	supportedBlockNodes: string[] = [],
	isBlockNodeSupported: InlineCommentAnnotationProvider['isBlockNodeSupported'],
	state: EditorState,
	dispatch?: CommandDispatch,
) => {
	const { tr, selection } = state;
	const selectedNodeIsSupported =
		selection instanceof NodeSelection &&
		(isSupportedBlockNode(selection.node, supportedBlockNodes, isBlockNodeSupported) ||
			(selection.node.type === state.schema.nodes.mediaSingle &&
				!!selection.node.firstChild &&
				isSupportedBlockNode(
					selection.node.firstChild,
					supportedBlockNodes,
					isBlockNodeSupported,
				)));

	if (selectedNodeIsSupported) {
		const { $from } = selection;
		let currNode = selection.node;
		let from = $from.start();

		// for media annotation, the selection is on media Single
		if (currNode.type === state.schema.nodes.mediaSingle && currNode.firstChild) {
			currNode = currNode.firstChild;
			from = from + 1;
		}

		const { annotation: annotationMarkType } = state.schema.marks;
		const hasAnnotation = currNode.marks.some((mark) => mark.type === annotationMarkType);

		if (!hasAnnotation) {
			return false;
		}

		tr.removeNodeMark(
			from,
			annotationMarkType.create({
				id,
				type: AnnotationTypes.INLINE_COMMENT,
			}),
		);

		if (dispatch) {
			dispatch(tr);
		}

		return true;
	}

	return false;
};

export const removeInlineCommentNearSelection =
	(
		id: string,
		supportedNodes: string[] = [],
		isBlockNodeSupported?: InlineCommentAnnotationProvider['isBlockNodeSupported'],
	): Command =>
	(state, dispatch): boolean => {
		const {
			tr,
			selection: { $from },
		} = state;

		if (removeInlineCommentFromNode(id, supportedNodes, isBlockNodeSupported, state, dispatch)) {
			return true;
		}

		const { annotation: annotationMarkType } = state.schema.marks;

		const hasAnnotation = $from.marks().some((mark) => mark.type === annotationMarkType);

		if (!hasAnnotation) {
			return false;
		}

		// just remove entire mark from around the node
		tr.removeMark(
			$from.start(),
			$from.end(),
			annotationMarkType.create({
				id,
				type: AnnotationTypes.INLINE_COMMENT,
			}),
		);

		if (dispatch) {
			dispatch(tr);
		}

		return true;
	};

export const removeInlineCommentFromDoc =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(
		id: string,
		supportedNodes: string[] = [],
		isBlockNodeSupported?: InlineCommentAnnotationProvider['isBlockNodeSupported'],
	): Command =>
	(state, dispatch): boolean => {
		const { tr } = state;

		state.doc.descendants((node: PMNode, pos: number) => {
			// Inline comment on mediaInline is not supported as part of comments on media project
			// Thus, we skip the decoration for mediaInline node
			if (node.type.name === 'mediaInline') {
				return false;
			}

			const isSupportedBlock =
				node.isBlock && isSupportedBlockNode(node, supportedNodes, isBlockNodeSupported);

			node.marks
				.filter((mark) => mark.type === state.schema.marks.annotation && mark.attrs.id === id)
				.forEach((mark) => {
					if (isSupportedBlock) {
						tr.removeNodeMark(pos, mark);
					} else {
						tr.removeMark(pos, pos + node.nodeSize, mark);
					}
				});
		});

		if (dispatch) {
			dispatch(transform.addDeleteAnalytics(editorAnalyticsAPI)(tr, state));
			return true;
		}

		return false;
	};

const getDraftCommandAction: (
	drafting: boolean,
	targetType: TargetType,
	targetNodeId?: string,
	supportedBlockNodes?: string[],
	isBlockNodeSupported?: InlineCommentAnnotationProvider['isBlockNodeSupported'],
	isOpeningMediaCommentFromToolbar?: boolean,
) => (state: Readonly<EditorState>) => InlineCommentAction | false = (
	drafting: boolean,
	targetType: TargetType,
	targetNodeId?: string,
	supportedBlockNodes?: string[],
	isBlockNodeSupported?: InlineCommentAnnotationProvider['isBlockNodeSupported'],
	isOpeningMediaCommentFromToolbar?: boolean,
) => {
	return (editorState: EditorState) => {
		// validate selection only when entering draft mode
		if (
			drafting &&
			isSelectionValid(editorState, supportedBlockNodes, isBlockNodeSupported) !==
				AnnotationSelectionType.VALID
		) {
			return false;
		}

		return {
			type: ACTIONS.SET_INLINE_COMMENT_DRAFT_STATE,
			data: {
				drafting,
				editorState,
				targetType,
				supportedBlockNodes,
				isBlockNodeSupported,
				targetNodeId,
				isOpeningMediaCommentFromToolbar,
			},
		};
	};
};

/**
 * Show active inline comments for a given block node, otherwise,
 * return false if the node has no comments or no unresolved comments.
 * @param supportedBlockNodes
 * @example
 */
export const showInlineCommentForBlockNode =
	(
		supportedBlockNodes: string[] = [],
		isBlockNodeSupported?: InlineCommentAnnotationProvider['isBlockNodeSupported'],
		annotationManager?: AnnotationManager,
	) =>
	(
		node: PMNode | null,
		viewMethod?: VIEW_METHOD,
		isOpeningMediaCommentFromToolbar?: boolean,
	): Command =>
	(state, dispatch) => {
		const pluginState = getPluginState(state);
		const { annotation } = state.schema.marks;

		if (
			node &&
			node.isBlock &&
			isSupportedBlockNode(node, supportedBlockNodes, isBlockNodeSupported)
		) {
			const unresolvedAnnotationMarks = (node?.marks || [])
				.filter((mark) => mark.type === annotation && !pluginState?.annotations[mark.attrs.id])
				.map((mark) => ({
					id: mark.attrs.id,
					type: mark.attrs.annotationType,
				}));

			if (unresolvedAnnotationMarks.length) {
				if (dispatch) {
					if (annotationManager) {
						// Selecting directly would bypass the preemptive gate, discarding any unsaved
						// comment. The plugin's view runs the gate on pending selections and flushes them.
						setPendingSelectedAnnotations(unresolvedAnnotationMarks, {
							selectAnnotationMethod: viewMethod,
							isOpeningMediaCommentFromToolbar,
						})(state, dispatch);
					} else {
						// bypass createCommand with setMeta
						// so that external plugins can be aware of if there are active(unresolved) comments associated with the node
						// i.e. media plugin can use the return result (true/false) to show toggle create comment component
						dispatch(
							state.tr.setMeta(inlineCommentPluginKey, {
								type: ACTIONS.SET_SELECTED_ANNOTATION,
								data: {
									selectedAnnotations: unresolvedAnnotationMarks,
									selectAnnotationMethod: viewMethod,
									isOpeningMediaCommentFromToolbar,
								},
							}),
						);
					}

					return true;
				}
			}
		}

		return false;
	};

export const setInlineCommentDraftState =
	(
		editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
		supportedBlockNodes: string[] = [],
		api?: ExtractInjectionAPI<AnnotationPlugin>,
		isBlockNodeSupported?: InlineCommentAnnotationProvider['isBlockNodeSupported'],
		createCommentExperience?: AnnotationProviders['createCommentExperience'],
	) =>
	(
		drafting: boolean,
		inputMethod: InlineCommentInputMethod = INPUT_METHOD.TOOLBAR,
		targetType: TargetType = 'inline',
		targetNodeId: string | undefined = undefined,
		isOpeningMediaCommentFromToolbar?: boolean,
	): Command => {
		const commandAction = getDraftCommandAction(
			drafting,
			targetType,
			targetNodeId,
			supportedBlockNodes,
			isBlockNodeSupported,
			isOpeningMediaCommentFromToolbar,
		);
		const draftCommand = !api?.toolbar
			? createCommand(
					commandAction,
					transform.handleDraftState(editorAnalyticsAPI)(drafting, inputMethod),
				)
			: undefined;

		return (state, dispatch, view) => {
			// Start tracking only when a valid draft is dispatched, never during dry runs.
			const dispatchDraft =
				dispatch &&
				drafting &&
				targetType === 'block' &&
				state.selection instanceof NodeSelection &&
				state.selection.node.type.name === 'extension' &&
				state.selection.node.attrs.extensionKey === `${NATIVE_EMBED_EXTENSION_KEY}:maui` &&
				fg('cc_maui_annotations_on_extensions')
					? (tr: Transaction) => {
							createCommentExperience?.start({
								attributes: {
									pageClass: 'editor',
									commentType: 'block',
									blockType: 'extension',
									commentTargetType: 'remixEmbed',
									entryPoint: 'highlightActions',
								},
							});
							createCommentExperience?.initExperience.start();
							dispatch(tr);
						}
					: dispatch;

			if (Boolean(api?.toolbar)) {
				const newPluginState = commandAction(state);
				if (!newPluginState) {
					return false;
				}
				const tr = transform.handleDraftState(editorAnalyticsAPI)(drafting, inputMethod)(
					state.tr,
					state,
				);

				if (tr && newPluginState) {
					tr.setMeta(inlineCommentPluginKey, newPluginState);

					if (drafting) {
						setUserIntent(api, tr);
					} else {
						resetUserIntent(api, tr);
					}

					if (dispatchDraft) {
						dispatchDraft(tr);
					}
				} else {
					return false;
				}
				return true;
			}
			return draftCommand ? draftCommand(state, dispatchDraft, view) : false;
		};
	};

export const addInlineComment =
	(
		editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
		editorAPI?: ExtractInjectionAPI<AnnotationPlugin> | undefined,
	) =>
	(
		id: string,
		supportedBlockNodes?: string[],
		isBlockNodeSupported?: InlineCommentAnnotationProvider['isBlockNodeSupported'],
	): Command => {
		const commandAction: (editorState: EditorState) => InlineCommentAction = (
			editorState: EditorState,
		) => ({
			type: ACTIONS.ADD_INLINE_COMMENT,
			data: {
				drafting: false,
				inlineComments: { [id]: false },
				// Auto make the newly inserted comment selected.
				// We move the selection to the head of the comment selection.
				selectedAnnotations: [{ id, type: AnnotationTypes.INLINE_COMMENT }],
				editorState,
			},
		});

		if (Boolean(editorAPI?.toolbar)) {
			return (state, dispatch) => {
				const tr = transform.addInlineComment(editorAnalyticsAPI, editorAPI)(
					id,
					supportedBlockNodes,
					isBlockNodeSupported,
				)(state.tr, state);

				tr.setMeta(inlineCommentPluginKey, commandAction(state));
				resetUserIntent(editorAPI, tr);

				if (dispatch) {
					dispatch(tr);

					return true;
				}

				return false;
			};
		} else {
			return createCommand(
				commandAction,
				transform.addInlineComment(editorAnalyticsAPI, editorAPI)(
					id,
					supportedBlockNodes,
					isBlockNodeSupported,
				),
			);
		}
	};

export const updateMouseState = (mouseData: InlineCommentMouseData): Command =>
	createCommand({
		type: ACTIONS.INLINE_COMMENT_UPDATE_MOUSE_STATE,
		data: { mouseData },
	});

export const setSelectedAnnotation = (id: string): Command =>
	createCommand({
		type: ACTIONS.SET_SELECTED_ANNOTATION,
		data: {
			selectedAnnotations: [{ id, type: AnnotationTypes.INLINE_COMMENT }],
		},
	});

export const setHoveredAnnotation = (id: string): Command =>
	createCommand({
		type: ACTIONS.SET_HOVERED_ANNOTATION,
		data: {
			hoveredAnnotations: [{ id, type: AnnotationTypes.INLINE_COMMENT }],
		},
	});

export const createAnnotation =
	(
		editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
		editorAPI?: ExtractInjectionAPI<AnnotationPlugin> | undefined,
	) =>
	(
		id: string,
		annotationType: AnnotationTypes = AnnotationTypes.INLINE_COMMENT,
		supportedBlockNodes?: string[],
		isBlockNodeSupported?: InlineCommentAnnotationProvider['isBlockNodeSupported'],
	): Command =>
	(state, dispatch) => {
		// don't try to add if there are is no temp highlight bookmarked
		const { bookmark } = getPluginState(state) || {};
		if (!bookmark || !dispatch) {
			return false;
		}

		if (annotationType === AnnotationTypes.INLINE_COMMENT) {
			return addInlineComment(editorAnalyticsAPI, editorAPI)(
				id,
				supportedBlockNodes,
				isBlockNodeSupported,
			)(state, dispatch);
		}

		return false;
	};

export const setInlineCommentsVisibility = (isVisible: boolean): Command => {
	return createCommand({
		type: ACTIONS.INLINE_COMMENT_SET_VISIBLE,
		data: { isVisible },
	});
};
